import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import toast from 'react-hot-toast';

import FullLayout from 'layout/FullLayout';
import { useInvoiceStatusQuery, useGetBalanceData } from 'pages/Checkout/hook/use-checkout';

const normalizeText = (value) =>
  String(value || '')
    .replace(/\s+/g, '')
    .toUpperCase();

const getInvoicePayload = (response) => {
  if (!response) return null;

  if (response?.data?.invoice_number) return response.data;
  if (response?.invoice_number) return response;

  return null;
};

const getStatusBadgeClasses = (status) => {
  if (status === 'PAID' || status === 'SUCCESS') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  if (status === 'ACTIVE') {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  if (status === 'EXPIRED' || status === 'FAILED' || status === 'CANCELLED') {
    return 'bg-red-50 text-red-700 border-red-200';
  }

  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const formatCurrencyIDR = (amount) => {
  const numericAmount = Number(amount || 0);
  return `Rp ${numericAmount.toLocaleString('id-ID')}`;
};

const formatDateTimeID = (value) => {
  if (!value) return '-';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getBankLogo = (bankName) => {
  const bankLogoMap = {
    BNI: '/bank/bni-logo.webp',
    BCA: '/bank/bca-logo.webp',
    MANDIRI: '/bank/mandiri-logo.webp',
    BRI: '/bank/bri-logo.webp',
    PERMATA: '/bank/permata-logo.webp',
    CIMB: '/bank/cimb.png',
    BSI: '/bank/bsi-logo.webp',
    SINARMAS: '/bank/sinarmas-logo.webp',
  };

  const normalized = normalizeText(bankName);
  const matchedKey = Object.keys(bankLogoMap).find((key) => normalized.includes(key));

  return matchedKey ? bankLogoMap[matchedKey] : null;
};

const InfoRow = ({ label, value, className = '' }) => (
  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p
      className={`break-all text-sm font-semibold text-gray-900 sm:max-w-[70%] sm:text-right ${className}`}>
      {value || '-'}
    </p>
  </div>
);

const InvoiceStatusNotice = ({ invoiceStatus }) => {
  if (invoiceStatus === 'EXPIRED') {
    return (
      <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm font-semibold text-red-900">Invoice sudah kedaluwarsa</p>
        <p className="mt-1 text-sm text-red-800">
          Silakan kembali ke checkout untuk membuat invoice baru.
        </p>
      </div>
    );
  }

  if (invoiceStatus === 'FAILED') {
    return (
      <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm font-semibold text-red-900">Pembayaran gagal</p>
        <p className="mt-1 text-sm text-red-800">
          Silakan coba lagi dengan membuat invoice baru atau ganti metode pembayaran.
        </p>
      </div>
    );
  }

  return null;
};

const InvoiceDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const invoiceNumber = searchParams.get('invoice_number');

  const { data: invoiceStatusResponse, isFetching: isCheckingInvoiceStatus } =
    useInvoiceStatusQuery(invoiceNumber, {
      enabled: Boolean(invoiceNumber),
      refetchInterval: 10000,
    });

  const invoiceData = getInvoicePayload(invoiceStatusResponse);

  const paymentMethodType = invoiceData?.payment_method?.type;
  const paymentMethodFlow = invoiceData?.payment_method?.flow;
  const paymentProvider = invoiceData?.payment_method?.provider;
  const paymentRedirect =
    invoiceData?.payment_method?.redirect_url || invoiceData?.payment_url || '';

  const isVirtualAccountInvoice = paymentMethodType === 'virtual_account';
  const isDanaInvoice = paymentProvider === 'DANA';
  const isDanaBindingFlow = isDanaInvoice && paymentMethodFlow === 'binding';
  const isDanaPaymentFlow = isDanaInvoice && paymentMethodFlow === 'payment';

  const invoiceStatusRaw =
    invoiceData?.invoice_status || invoiceData?.status || invoiceData?.payment_status || 'PENDING';
  const invoiceStatus = String(invoiceStatusRaw).toUpperCase();

  const isInvoicePaid =
    Boolean(invoiceData?.is_paid) || invoiceStatus === 'PAID' || invoiceStatus === 'SUCCESS';
  const isExpiredInvoice = invoiceStatus === 'EXPIRED';
  const isFailedInvoice = invoiceStatus === 'FAILED';
  const isInvoiceInactive = isExpiredInvoice || isFailedInvoice;

  const displayedBankName = invoiceData?.payment_method?.bank;
  const displayedAccountNumber = invoiceData?.payment_method?.account_number;
  const displayedBankLogo = getBankLogo(displayedBankName);
  const danaRedirectUrl =
    invoiceData?.payment_method?.redirect_url || invoiceData?.payment_url || '';

  const shouldShowExpiredAt =
    Boolean(invoiceData?.expired_at) && (Boolean(displayedAccountNumber) || isDanaPaymentFlow);

  useEffect(() => {
    if (!invoiceNumber) {
      navigate('/checkout');
    }
  }, [invoiceNumber, navigate]);

  useEffect(() => {
    if (isInvoicePaid) {
      navigate('/thank-you');
    }
  }, [isInvoicePaid, navigate]);

  const handleCopyVirtualAccount = async () => {
    if (!displayedAccountNumber) return;

    try {
      await navigator.clipboard.writeText(displayedAccountNumber);
      toast.success('Nomor VA berhasil disalin');
    } catch {
      toast.error('Gagal menyalin nomor VA');
    }
  };

  const handleDanaAction = () => {
    if (!danaRedirectUrl || isInvoiceInactive) return;
    window.open(danaRedirectUrl, '_self');
  };

  const handleContactSupport = () => {
    const phoneNumber = '6281290904616';
    const message = encodeURIComponent(
      `Halo Admin KIN, saya membutuhkan bantuan terkait pembayaran.` +
        `\nInvoice: ${invoiceData?.invoice_number || '-'} ` +
        `\nNomor VA: ${displayedAccountNumber || '-'} ` +
        `\nTotal: ${formatCurrencyIDR(invoiceData?.total)}`,
    );

    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  if (!invoiceNumber) {
    return (
      <FullLayout>
        <div className="max-w-3xl mx-auto pt-10 pb-24 px-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Nomor invoice tidak ditemukan</h2>
            <p className="mt-1 text-sm text-gray-600">
              Silakan kembali ke checkout dan buat invoice terlebih dahulu.
            </p>
            <button
              type="button"
              onClick={() => navigate('/checkout')}
              className="mt-4 bg-primary-main text-white rounded-[100px] px-4 py-2 text-sm font-medium">
              Kembali ke Checkout
            </button>
          </div>
        </div>
      </FullLayout>
    );
  }

  if (!invoiceData) {
    return (
      <FullLayout>
        <div className="max-w-3xl mx-auto pt-10 pb-24 px-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex items-center gap-3">
            <CircularProgress size={20} />
            <p className="text-sm text-gray-600">Memuat detail invoice...</p>
          </div>
        </div>
      </FullLayout>
    );
  }

  return (
    <FullLayout>
      <div className="max-w-7xl mx-auto pt-10 pb-24 px-4 text-kin-text">
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-10 gap-6">
          <div className="w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Invoice Detail</h3>
            <p className="mt-1 text-sm text-gray-600">
              {isVirtualAccountInvoice
                ? 'Silakan selesaikan pembayaran Virtual Account sebelum batas waktu.'
                : 'Lanjutkan proses pembayaran melalui DANA.'}
            </p>

            <InvoiceStatusNotice invoiceStatus={invoiceStatus} />

            {isVirtualAccountInvoice && (
              <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-500">Bank</p>
                  {displayedBankLogo ? (
                    <img
                      src={displayedBankLogo}
                      alt={displayedBankName || 'Bank logo'}
                      className="h-8 w-auto object-contain"
                    />
                  ) : (
                    <p className="text-sm font-semibold text-gray-900">
                      {displayedBankName || '-'}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-500">Nomor Virtual Account</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="break-all text-xl font-semibold text-gray-900">
                      {displayedAccountNumber || '-'}
                    </p>
                    <button
                      type="button"
                      onClick={handleCopyVirtualAccount}
                      disabled={!displayedAccountNumber || isInvoiceInactive}
                      className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400">
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isDanaInvoice && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-900">
                  {isDanaBindingFlow ? 'Akun DANA perlu dihubungkan' : 'Lanjutkan pembayaran DANA'}
                </p>
                <p className="mt-1 text-sm text-amber-800">
                  {isDanaBindingFlow
                    ? 'Klik tombol di bawah untuk menghubungkan akun DANA kamu.'
                    : 'Klik tombol di bawah untuk kembali ke halaman pembayaran DANA.'}
                </p>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-gray-200 bg-white">
              <InfoRow label="Invoice Number" value={invoiceData?.invoice_number} />
              <div className="border-t border-gray-200">
                <InfoRow
                  label="Total Payment"
                  value={formatCurrencyIDR(invoiceData?.total)}
                  className="text-base"
                />
              </div>
              {shouldShowExpiredAt && (
                <div className="border-t border-gray-200">
                  <InfoRow
                    label="Berlaku Sampai"
                    value={formatDateTimeID(invoiceData?.expired_at)}
                  />
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {isVirtualAccountInvoice && (
                <button
                  type="button"
                  onClick={handleCopyVirtualAccount}
                  disabled={!displayedAccountNumber || isInvoiceInactive}
                  className="bg-primary-main disabled:bg-gray-300 text-white rounded-[100px] px-4 py-2 text-sm font-medium">
                  Copy VA Number
                </button>
              )}

              {isDanaInvoice && (
                <button
                  type="button"
                  onClick={handleDanaAction}
                  disabled={!danaRedirectUrl || isInvoiceInactive}
                  className="bg-primary-main disabled:bg-gray-300 text-white rounded-[100px] px-4 py-2 text-sm font-medium">
                  {isDanaBindingFlow ? 'Hubungkan ke DANA' : 'Lanjutkan Pembayaran DANA'}
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-gray-200 flex flex-col gap-4 p-5 text-kin-text mt-6 lg:mt-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Status Pembayaran</h3>
              <p className="mt-1 text-sm text-gray-600">
                Status invoice diperbarui otomatis setiap 10 detik.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-sm font-semibold text-gray-900">Invoice Status</p>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClasses(
                    invoiceStatus,
                  )}`}>
                  {invoiceStatus}
                </span>
                {isCheckingInvoiceStatus && (
                  <span className="text-xs text-gray-500">Checking latest status...</span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">Ringkasan</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">Invoice</span>
                  <span className="text-right font-medium text-gray-800 break-all">
                    {invoiceData?.invoice_number || '-'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-500">Total</span>
                  <span className="text-right font-semibold text-gray-900">
                    {formatCurrencyIDR(invoiceData?.total)}
                  </span>
                </div>
                {shouldShowExpiredAt && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">Berlaku Sampai</span>
                    <span className="text-right font-medium text-gray-800">
                      {formatDateTimeID(invoiceData?.expired_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">Cara Pembayaran</p>
              <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-700">
                {isExpiredInvoice || isFailedInvoice ? (
                  <>
                    <li>Periksa status invoice pada bagian status pembayaran.</li>
                    <li>Kembali ke halaman checkout untuk membuat invoice baru.</li>
                    <li>Pastikan metode pembayaran sudah sesuai sebelum submit ulang.</li>
                  </>
                ) : isDanaInvoice ? (
                  <>
                    <li>Klik tombol aksi DANA untuk membuka aplikasi/halaman DANA.</li>
                    <li>Login atau konfirmasi akun DANA, lalu selesaikan proses pembayaran.</li>
                    <li>Kembali ke halaman ini untuk melihat status pembayaran terbaru.</li>
                  </>
                ) : (
                  <>
                    <li>Buka mobile banking atau ATM yang mendukung Virtual Account.</li>
                    <li>Pilih menu pembayaran Virtual Account.</li>
                    <li>Masukkan nomor VA dan konfirmasi nominal pembayaran.</li>
                    <li>Selesaikan transaksi dan simpan bukti pembayaran.</li>
                  </>
                )}
              </ol>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleContactSupport}
                className="bg-[#25D366] text-white rounded-[100px] py-2 px-4 flex items-center gap-1.5 text-sm font-medium hover:bg-[#20BA5A]">
                <IconBrandWhatsapp className="w-4 h-4" />
                Need Help?
              </button>
            </div>
          </div>
        </div>
      </div>
    </FullLayout>
  );
};

export default InvoiceDetailPage;
